import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, Package } from "lucide-react";
import { trackEvent } from "../lib/analytics";

export default function OrderSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: null, paymentStatus: null, order: null, error: null, timedOut: false });
  const attempts = useRef(0);
  const MAX_ATTEMPTS = 8;

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, error: "Missing session", timedOut: false }); return; }

    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        const isPaid = data.payment_status === "paid";
        const isExpired = data.status === "expired";
        const exhausted = attempts.current >= MAX_ATTEMPTS;

        setState({
          loading: false,
          status: data.status,
          paymentStatus: data.payment_status,
          order: data.order,
          error: null,
          timedOut: !isPaid && (isExpired || exhausted),
        });

        if (isPaid) {
          trackEvent("Purchase", { value: data.amount_total / 100, currency: data.currency });
          return;
        }
        if (isExpired) return;
        if (!exhausted) {
          attempts.current += 1;
          setTimeout(poll, 2000);
        }
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: "Could not verify payment", timedOut: true });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const stillWaiting = state.loading || (state.paymentStatus === "unpaid" && !state.timedOut);

  return (
    <div data-testid="order-success-page" className="max-w-3xl mx-auto px-6 py-24 text-center">
      {stillWaiting ? (
        <div>
          <Loader2 size={40} className="animate-spin mx-auto mb-6 opacity-60" />
          <h1 className="font-serif-display text-3xl mb-3">Confirming your payment…</h1>
          <p className="opacity-70">This usually takes a few seconds.</p>
        </div>
      ) : state.paymentStatus === "paid" ? (
        <div>
          <CheckCircle2 size={48} style={{ color: "var(--kaiya-success)" }} className="mx-auto mb-6" />
          <h1 className="font-serif-display text-4xl md:text-5xl mb-4">Thank you.</h1>
          <p className="opacity-80 mb-8">Your order is on its way. A confirmation is heading to your inbox.</p>
          {state.order && (
            <div className="kaiya-card p-8 text-left mb-8" data-testid="order-summary">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="label-eyebrow">Order</div>
                  <div className="font-mono text-sm mt-1">#{state.order.id?.slice(0, 8)}</div>
                </div>
                <div className="font-serif-display text-2xl">${state.order.total?.toFixed(2)} {state.order.currency}</div>
              </div>
              {state.order.shipment && (
                <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--kaiya-bg-2)" }}>
                  <Package size={18} />
                  <div className="text-sm">
                    <div className="font-medium">Chit Chats tracking</div>
                    <div className="opacity-70 font-mono">{state.order.shipment.tracking_code}</div>
                  </div>
                </div>
              )}
              <div className="mt-6 space-y-3">
                {state.order.line_items?.map((li, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div>{li.name} {li.variant && <span className="opacity-60">• {li.variant}</span>} <span className="opacity-60">×{li.quantity}</span></div>
                    <div>${li.line_total?.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Link to="/shop" className="btn-primary inline-block">Keep shopping</Link>
        </div>
      ) : (
        <div>
          <XCircle size={48} style={{ color: "var(--kaiya-error)" }} className="mx-auto mb-6" />
          <h1 className="font-serif-display text-3xl mb-3">We couldn't confirm your payment.</h1>
          <p className="opacity-70 mb-6">
            {state.error || "If you did complete payment, please check your email for the Stripe receipt or contact hello@kaiyayoga.com — we can look it up by your email."}
          </p>
          <Link to="/checkout" className="btn-primary inline-block">Try again</Link>
        </div>
      )}
    </div>
  );
}
